// Origin resolver. Spec 5.1.
//
// GPS gives you a point. That point is not enough. Underground there is no
// GPS at all, so a stale fix will confidently plan a journey from where you
// were twenty minutes ago.
import { useCallback, useEffect, useState } from 'react';
import { loadLastFix, saveLastFix } from './store';

// Worse than this and we do not trust the fix. Spec 5.1.
const ACCURACY_LIMIT_METRES = 100;
// Older than this and we say so rather than pretending it is current.
const STALE_AFTER_MS = 5 * 60_000;

export interface Origin {
  lat: number;
  lon: number;
  confidence: 'gps' | 'stale' | 'pinned' | 'unknown';
  accuracyM?: number;
  ageMs?: number;
  /** A pinned saved place beats everything, including a perfect fix. */
  pinnedPlaceId?: string;
}

/**
 * `pinnedPlaceId` null means "my location", which is the default everywhere
 * now, including desktop. Desktop geolocation is coarse, but coarse and
 * honest beats a pin that is silently wrong on the day you work elsewhere,
 * and the accuracy check below downgrades it rather than trusting it.
 */
export function useOrigin(pinnedPlaceId: string | null, fallbackPlaceId?: string) {
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [error, setError] = useState<string | null>(null);

  const locate = useCallback(() => {
    // Spec 5.1: an explicit pin beats any amount of clever positioning.
    if (pinnedPlaceId) {
      setOrigin({ lat: 0, lon: 0, confidence: 'pinned', pinnedPlaceId });
      return;
    }

    if (!('geolocation' in navigator)) {
      void fallback('no geolocation on this device');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (accuracy > ACCURACY_LIMIT_METRES) {
          void fallback(`fix is only accurate to ${Math.round(accuracy)}m`);
          return;
        }
        setError(null);
        setOrigin({ lat: latitude, lon: longitude, confidence: 'gps', accuracyM: accuracy });
        void saveLastFix({ lat: latitude, lon: longitude, at: Date.now() });
      },
      (err) => void fallback(err.message),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 }
    );

    async function fallback(reason: string) {
      const last = await loadLastFix();
      if (!last) {
        // Never leave the user with nothing. If GPS is unavailable and there
        // is no history, fall back to their saved default place and say so.
        if (fallbackPlaceId) {
          setError(reason);
          setOrigin({ lat: 0, lon: 0, confidence: 'pinned', pinnedPlaceId: fallbackPlaceId });
          return;
        }
        setError(`${reason}, and no previous position saved`);
        setOrigin(null);
        return;
      }
      const ageMs = Date.now() - last.at;
      setError(reason);
      setOrigin({
        lat: last.lat,
        lon: last.lon,
        // Spec 5.1: show it as an assumption the user can tap to correct.
        confidence: ageMs > STALE_AFTER_MS ? 'stale' : 'gps',
        ageMs,
      });
    }
  }, [pinnedPlaceId, fallbackPlaceId]);

  useEffect(() => {
    locate();
  }, [locate]);

  return { origin, error, relocate: locate };
}

export function describeOrigin(o: Origin | null, label: string): string {
  if (!o) return 'Working out where you are';
  if (o.confidence === 'pinned') return label;
  if (o.confidence === 'stale') {
    const mins = Math.round((o.ageMs ?? 0) / 60_000);
    return `${label}, from a fix ${mins} min old`;
  }
  return label;
}
